import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import { haptic } from '@/lib/telegram';
import styles from './ErrorBoundary.module.css';

interface Props {
  children: ReactNode;
  /** Кастомный фолбэк (если не указан — используется встроенный) */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Здесь будет Sentry/Datadog, пока — только в консоль
    console.error('[ErrorBoundary]', error, info);

    // Тактильный отклик — пользователю становится понятно, что произошло
    try {
      haptic.error();
    } catch {}
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      return <DefaultErrorScreen error={this.state.error} onReset={this.reset} />;
    }
    return this.props.children;
  }
}

// ── Стандартный экран ошибки ────────────────────────────
const DefaultErrorScreen = ({ error, onReset }: { error: Error; onReset: () => void }) => {
  const handleReload = () => {
    haptic.medium();
    window.location.reload();
  };

  const handleGoHome = () => {
    haptic.light();
    onReset();
    window.location.href = '/';
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.iconWrap}>
        <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 8v4M12 16h.01" />
        </svg>
      </div>

      <p className={styles.eyebrow}>ЧТО-ТО ПОШЛО НЕ ТАК</p>
      <h2 className={styles.title}>Произошла ошибка</h2>
      <p className={styles.body}>
        Что-то сломалось во время работы приложения. Мы уже знаем о проблеме
        и работаем над ней.
      </p>

      {/* Технические детали — только в DEV */}
      {import.meta.env.DEV && (
        <details className={styles.details}>
          <summary className={styles.detailsSummary}>Технические детали</summary>
          <pre className={styles.errorText}>
            {error.name}: {error.message}
            {'\n\n'}
            {error.stack}
          </pre>
        </details>
      )}

      <div className={styles.actions}>
        <Button variant="primary" onClick={handleReload} fullWidth>
          Перезагрузить
        </Button>
        <Button variant="secondary" onClick={handleGoHome} fullWidth>
          На главную
        </Button>
      </div>
    </div>
  );
};